import { SlidersHorizontal } from '@phosphor-icons/react';
import { Card } from 'src/components/Card';
import { useHasActiveProviderConnection } from 'src/hooks/useHasActiveProviderConnection';
import { CrmProviderSelect } from 'src/pages/Automations/components/ui/CrmProviderSelect';
import { useWizard } from 'src/pages/Automations/components/wizard/WizardContext';
import { NON_AUTO_SYNCABLE_PROVIDERS } from 'src/services/integration-app.service';

export default function CRMIntegration({
  className = '',
}: {
  className?: string;
}) {
  const { campaignAutomation, setCampaignAutomation } = useWizard();
  const isCrmProviderAuthenticated = useHasActiveProviderConnection(
    campaignAutomation.crmProviderOption.value,
  );

  return (
    <div className={`space-y-8 ${className}`}>
      <div>
        <h2 className="text-2xl font-bold mb-2">CRM Integration</h2>
        <p className="text-neutral-600 mb-6">
          Connect your CRM to import contacts into this campaign.
        </p>

        <Card className="p-6 bg-neutral-50 border-neutral-100">
          <CrmProviderSelect
            onChange={(providerOption) => {
              const isNonAutoSyncable = NON_AUTO_SYNCABLE_PROVIDERS.includes(
                providerOption.value,
              );
              setCampaignAutomation({
                ...campaignAutomation,
                crmProviderOption: providerOption,
                ...(isNonAutoSyncable && { isAutoSyncContactsEnabled: false }),
              });
            }}
            selectedProvider={campaignAutomation.crmProviderOption}
            isAuthenticated={!!isCrmProviderAuthenticated}
          />

          {campaignAutomation.crmProviderOption.value &&
            isCrmProviderAuthenticated && (
              <>
                <div className="p-4 mb-6 border border-neutral-200 rounded-md bg-neutral-50">
                  <div className="flex items-center mb-4">
                    <SlidersHorizontal className="h-6 w-6 text-primary mr-2" />
                    <h4>Integration Settings</h4>
                  </div>
                  <div className="mb-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        disabled={NON_AUTO_SYNCABLE_PROVIDERS.includes(
                          campaignAutomation.crmProviderOption.value,
                        )}
                        checked={
                          !NON_AUTO_SYNCABLE_PROVIDERS.includes(
                            campaignAutomation.crmProviderOption.value,
                          ) && !!campaignAutomation.isAutoSyncContactsEnabled
                        }
                        onChange={(e) =>
                          setCampaignAutomation({
                            ...campaignAutomation,
                            isAutoSyncContactsEnabled: e.target.checked,
                          })
                        }
                      />
                      <span className="text-sm font-medium text-neutral-600">
                        Automatically sync contacts into this campaign
                      </span>
                    </label>
                    <p className="text-xs text-neutral-400 ml-6">
                      Keep this campaign up to date by automatically adding all
                      new CRM contacts to the campaign.
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={!!campaignAutomation.recordCommunicationsInCrm}
                        onChange={(e) =>
                          setCampaignAutomation({
                            ...campaignAutomation,
                            recordCommunicationsInCrm: e.target.checked,
                          })
                        }
                      />
                      <span className="text-sm font-medium text-neutral-600">
                        Record communications in CRM
                      </span>
                    </label>
                    <p className="text-xs text-neutral-400 ml-6">
                      Automatically log outreach attempts and call transcripts
                      to your CRM.
                    </p>
                  </div>
                </div>
              </>
            )}
        </Card>
      </div>
    </div>
  );
}
